import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Sparkles, ShieldCheck, ArrowRight, X, HeartHandshake } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Pre-checkout trust modal — appears when the user clicks "Buy" / "Continue".
 *
 * Combination behaviour (per product spec):
 *   • Always shown on first encounter.
 *   • Has a "Don't show this again" checkbox that persists in localStorage.
 *   • Auto-skip for signed-in users who already completed an assessment
 *     AND ticked "skip pre-checkout next time" previously.
 *
 * Designed for emotional warmth, not pressure. Four options:
 *   1. Take the free assessment    (curiosity-led, lowest friction)
 *   2. Book a free consultation     (human guidance)
 *   3. WhatsApp our coach           (instant help)
 *   4. Continue to secure checkout  (the actual purchase, last on purpose)
 */
const LS_KEY = "fg_skip_pre_checkout_v1";


function shouldAutoSkip({ user }) {
  if (typeof window === "undefined") return false;
  try {
    const flag = localStorage.getItem(LS_KEY);
    if (flag !== "1") return false;
  } catch { return false; }
  // Only auto-skip for signed-in users who completed an assessment.
  if (!user) return false;
  if (!user.assessment_completed_at && !user.has_assessment) return false;
  return true;
}


export default function PreCheckoutModal({ open, onClose, product, purchaseUrl }) {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [skipNext, setSkipNext] = useState(false);

  // Auto-skip path — fires the redirect immediately and never renders.
  useEffect(() => {
    if (!open) return;
    if (shouldAutoSkip({ user })) {
      window.location.href = purchaseUrl;
    }
  }, [open, user, purchaseUrl]);

  if (!open) return null;
  if (shouldAutoSkip({ user })) return null;

  const continueToCheckout = () => {
    if (skipNext) {
      try { localStorage.setItem(LS_KEY, "1"); } catch { /* ignore */ }
    }
    window.location.href = purchaseUrl;
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-emerald-950/45 backdrop-blur-sm p-3 sm:p-6 animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="precheckout-title"
      data-testid="pre-checkout-modal"
    >
      <div className="w-full sm:max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden animate-in slide-in-from-bottom-6 sm:zoom-in-95 duration-300">
        {/* Header */}
        <div className="p-6 sm:p-9 pb-4">
          <div className="flex items-start justify-between gap-3 mb-2">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-700">
              <HeartHandshake className="w-5 h-5" strokeWidth={1.6} />
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-emerald-900/40 hover:text-emerald-900 transition"
              data-testid="pre-checkout-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">Before you continue</p>
          <h2 id="precheckout-title" className="font-display text-2xl sm:text-3xl text-emerald-950 font-light leading-tight mt-1">
            We're here to guide you, not sell to you.
          </h2>
          {product?.name && (
            <p className="text-sm text-emerald-900/70 mt-3 leading-relaxed">
              You're about to head to checkout for <strong className="text-emerald-950">{product.name}</strong>.
              Pause for a moment — many people find one of these gentler paths is a better fit.
            </p>
          )}
        </div>

        {/* Options */}
        <div className="px-6 sm:px-9 pb-3 grid sm:grid-cols-2 gap-2.5">
          <OptionCard
            icon={Sparkles}
            badge="Most popular"
            title="Take the free assessment"
            body="A 2-minute reflection that quietly tells us what your body is asking for."
            onClick={() => { onClose?.(); navigate("/questionnaire"); }}
            testid="pre-checkout-assessment"
          />
          <OptionCard
            icon={MessageCircle}
            title="WhatsApp our coach"
            body="A real human will help you choose — no script, no pressure."
            onClick={() => { window.open("https://wa.me/447424225333?text=Hi%2C%20I%27d%20like%20guidance%20choosing%20a%20product", "_blank", "noopener"); }}
            testid="pre-checkout-whatsapp"
          />
          <OptionCard
            icon={ShieldCheck}
            title="Book a 1:1 mentor call"
            body="15 calm minutes with a wellness mentor. Nothing to commit to."
            onClick={() => { onClose?.(); navigate("/contact?intent=mentor"); }}
            testid="pre-checkout-mentor"
          />
          <OptionCard
            icon={ArrowRight}
            title="Continue to secure checkout"
            body="I know what I want. Take me straight there."
            primary
            onClick={continueToCheckout}
            testid="pre-checkout-continue"
          />
        </div>

        {/* Skip-next-time + footnote */}
        <div className="px-6 sm:px-9 py-4 border-t border-emerald-100 bg-emerald-50/30 flex flex-col gap-1.5">
          <label className="flex items-center gap-2 text-[12px] text-emerald-900/75 cursor-pointer select-none" data-testid="pre-checkout-skip-label">
            <input
              type="checkbox"
              checked={skipNext}
              onChange={(e) => setSkipNext(e.target.checked)}
              className="accent-emerald-700"
              data-testid="pre-checkout-skip-checkbox"
            />
            Don't show this again
            {user ? "" : " (sign in to remember across devices)"}
          </label>
          <p className="text-[11px] text-emerald-900/55 leading-relaxed">
            Checkout is handled securely by Unicity. Feel Greats is the guidance layer — we don't
            store your payment details.
          </p>
        </div>
      </div>
    </div>
  );
}


function OptionCard({ icon: Icon, badge, title, body, onClick, primary, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative text-left rounded-2xl border p-4 transition ${primary
        ? "border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800"
        : "border-emerald-100 bg-white hover:border-emerald-300 hover:shadow-sm"}`}
      data-testid={testid}
    >
      {badge && !primary && (
        <span className="absolute top-2 right-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">
          {badge}
        </span>
      )}
      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl mb-2 ${primary ? "bg-white/15 text-white" : "bg-emerald-50 text-emerald-700"}`}>
        <Icon className="w-4 h-4" strokeWidth={1.6} />
      </span>
      <div className={`text-sm font-semibold ${primary ? "text-white" : "text-emerald-950"}`}>{title}</div>
      <div className={`text-[12px] mt-0.5 leading-relaxed ${primary ? "text-white/85" : "text-emerald-900/70"}`}>{body}</div>
    </button>
  );
}
